/**
 * Device Status Notification Component
 * Displays the last seen status of student devices as a popup overlay
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Chip,
  IconButton,
  Typography,
  CircularProgress,
  Tooltip,
  Stack,
  Paper,
  useTheme,
  Fade,
} from '@mui/material';
import {
  Close as CloseIcon,
  Devices as DevicesIcon,
  Phone as PhoneIcon,
  Watch as WatchIcon,
  DirectionsCar as VehicleIcon,
  CheckCircle as OnlineIcon,
  Error as OfflineIcon,
  Warning as WarningIcon,
  Battery20 as BatteryLowIcon,
  BatteryFull as BatteryFullIcon,
} from '@mui/icons-material';
import { getMyDevices, StudentDevice, StudentDevicesResponse } from '../services/itineraryApi';

interface DeviceStatusNotificationProps {
  onClose?: () => void;
}

const DeviceStatusNotification: React.FC<DeviceStatusNotificationProps> = ({ onClose }) => {
  const theme = useTheme();
  const [deviceData, setDeviceData] = useState<StudentDevicesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(true);

  useEffect(() => {
    const fetchDeviceStatus = async () => {
      try {
        setLoading(true);
        const data = await getMyDevices();
        setDeviceData(data);
        setError(null);
      } catch (err: any) {
        console.error('Failed to fetch device status:', err);
        setError(err.response?.data?.detail || 'Failed to load device status');
      } finally {
        setLoading(false);
      }
    };

    fetchDeviceStatus();
  }, []);

  const handleClose = () => {
    setOpen(false);
    if (onClose) {
      onClose();
    }
  };

  const getDeviceIcon = (deviceType: string) => {
    switch (deviceType.toLowerCase()) {
      case 'phone':
        return <PhoneIcon fontSize="small" />;
      case 'watch':
        return <WatchIcon fontSize="small" />;
      case 'vehicle':
        return <VehicleIcon fontSize="small" />;
      default:
        return <DevicesIcon fontSize="small" />;
    }
  };

  const getStatusColor = (device: StudentDevice): 'success' | 'warning' | 'error' | 'default' => {
    if (!device.has_location) return 'default';
    if (device.is_online) return 'success';
    if (device.last_seen_minutes_ago !== null && device.last_seen_minutes_ago < 60) return 'warning';
    return 'error';
  };

  const getStatusIcon = (device: StudentDevice) => {
    if (!device.has_location) return <OfflineIcon fontSize="small" />;
    if (device.is_online) return <OnlineIcon fontSize="small" />;
    if (device.last_seen_minutes_ago !== null && device.last_seen_minutes_ago < 60) return <WarningIcon fontSize="small" />;
    return <OfflineIcon fontSize="small" />;
  };

  const formatLastSeen = (minutesAgo: number | null): string => {
    if (minutesAgo === null) return 'Never';
    if (minutesAgo < 1) return 'Just now';
    if (minutesAgo < 60) return `${Math.floor(minutesAgo)} min ago`;
    if (minutesAgo < 1440) return `${Math.floor(minutesAgo / 60)} hours ago`;
    return `${Math.floor(minutesAgo / 1440)} days ago`;
  };

  const getBatteryIcon = (level?: number) => {
    if (level === undefined || level === null) return null;
    if (level <= 20) return <BatteryLowIcon fontSize="small" color="error" />;
    return <BatteryFullIcon fontSize="small" color="success" />;
  };

  if (!open) return null;

  // Get status color for the header
  const getHeaderColor = () => {
    if (!deviceData) return theme.palette.info.main;
    const allOnline = deviceData.online_devices === deviceData.total_devices;
    const someOnline = deviceData.online_devices > 0 && deviceData.online_devices < deviceData.total_devices;
    if (allOnline) return theme.palette.success.main;
    if (someOnline) return theme.palette.warning.main;
    return theme.palette.error.main;
  };

  return (
    <Fade in={open}>
      <Paper
        elevation={8}
        sx={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 1200,
          minWidth: 380,
          maxWidth: 450,
          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(30, 30, 35, 0.98)' : 'rgba(255, 255, 255, 0.98)',
          backdropFilter: 'blur(8px)',
          borderRadius: 2,
          overflow: 'hidden',
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1.5,
            backgroundColor: getHeaderColor(),
            color: 'white',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DevicesIcon fontSize="small" />
            <Typography variant="subtitle2" fontWeight={600}>
              Device Status
              {deviceData?.student_number && ` - ${deviceData.student_number}`}
              {deviceData?.course_id && ` (${deviceData.course_id})`}
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={handleClose}
            sx={{ color: 'white', p: 0.5 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ px: 2, py: 1.5 }}>
          {loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">
                Loading device status...
              </Typography>
            </Box>
          ) : error ? (
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          ) : !deviceData || deviceData.devices.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No devices registered for your account.
            </Typography>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                {deviceData.online_devices} of {deviceData.total_devices} device(s) online
              </Typography>

              <Stack spacing={1}>
                {deviceData.devices.map((device, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      p: 1.25,
                      bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                      borderRadius: 1,
                      border: 1,
                      borderColor: 'divider',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 75 }}>
                      {getDeviceIcon(device.device_type)}
                      <Typography variant="body2" sx={{ ml: 0.5, fontWeight: 500, textTransform: 'capitalize' }}>
                        {device.device_type}
                      </Typography>
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          display: 'block', 
                          fontFamily: 'monospace', 
                          fontSize: '0.7rem',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {device.device_id}
                      </Typography>
                      {device.serial_number && (
                        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontSize: '0.7rem' }}>
                          S/N: {device.serial_number}
                        </Typography>
                      )}
                      {device.phone_number && (
                        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontSize: '0.7rem' }}>
                          Phone: {device.phone_number}
                        </Typography>
                      )}
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                      {device.battery_level !== undefined && device.battery_level !== null && (
                        <Tooltip title={`Battery: ${device.battery_level}%`}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                            {getBatteryIcon(device.battery_level)}
                            <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>{device.battery_level}%</Typography>
                          </Box>
                        </Tooltip>
                      )}

                      <Chip
                        icon={getStatusIcon(device)}
                        label={formatLastSeen(device.last_seen_minutes_ago)}
                        size="small"
                        color={getStatusColor(device)}
                        variant={device.is_online ? 'filled' : 'outlined'}
                        sx={{ 
                          minWidth: 90, 
                          height: 26,
                          '& .MuiChip-label': { fontSize: '0.7rem', px: 1 },
                          '& .MuiChip-icon': { fontSize: '0.9rem' },
                        }}
                      />
                    </Box>
                  </Box>
                ))}
              </Stack>
            </>
          )}
        </Box>
      </Paper>
    </Fade>
  );
};

export default DeviceStatusNotification;

